import React, { useState, useEffect, useCallback, useRef } from 'react';

const TITLE_CHARS = ['9', '0', '"'];
const SUBTITLE_CHARS = ['T', 'O', ' ', 'W', 'I', 'N'];
const CANON_SOUND_URL = '/sounds/sf_canon_01.mp3';

export default function SplashScreen({ onLogin, onDemo }) {
  const [titleIndex, setTitleIndex] = useState(0);
  const [subtitleIndex, setSubtitleIndex] = useState(0);
  const [phase, setPhase] = useState('title'); // 'title', 'subtitle', 'done'
  const [showEnter, setShowEnter] = useState(false);
  const [audioStarted, setAudioStarted] = useState(false);
  const audioPoolRef = useRef([]);

  // Pre-load audio pool for rapid fire
  const initAudioPool = useCallback(() => {
    const pool = [];
    for (let i = 0; i < 12; i++) {
      const audio = new Audio(CANON_SOUND_URL);
      audio.volume = 0.6;
      audio.load();
      pool.push(audio);
    }
    audioPoolRef.current = pool;
  }, []);

  const poolIndexRef = useRef(0);

  const playCanon = useCallback(() => {
    const pool = audioPoolRef.current;
    if (pool.length === 0) return;
    // Round-robin through the pool to ensure each shot uses a different instance
    const idx = poolIndexRef.current % pool.length;
    poolIndexRef.current++;
    const audio = pool[idx];
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }, []);

  // Start animation on first user interaction (to allow audio)
  const startAnimation = useCallback(() => {
    if (audioStarted) return;
    initAudioPool();
    setAudioStarted(true);
  }, [audioStarted, initAudioPool]);

  useEffect(() => {
    if (!audioStarted) return;

    if (phase === 'title' && titleIndex < TITLE_CHARS.length) {
      const timeout = setTimeout(() => {
        playCanon();
        setTitleIndex(prev => prev + 1);
      }, 250);
      return () => clearTimeout(timeout);
    }

    if (phase === 'title' && titleIndex >= TITLE_CHARS.length) {
      const timeout = setTimeout(() => setPhase('subtitle'), 400);
      return () => clearTimeout(timeout);
    }

    if (phase === 'subtitle' && subtitleIndex < SUBTITLE_CHARS.length) {
      const timeout = setTimeout(() => {
        if (SUBTITLE_CHARS[subtitleIndex] !== ' ') {
          playCanon();
        }
        setSubtitleIndex(prev => prev + 1);
      }, 250);
      return () => clearTimeout(timeout);
    }

    if (phase === 'subtitle' && subtitleIndex >= SUBTITLE_CHARS.length) {
      const timeout = setTimeout(() => {
        setPhase('done');
        setShowEnter(true);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [audioStarted, phase, titleIndex, subtitleIndex, playCanon]);

  return (
    <div
      className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-4 relative overflow-hidden cursor-pointer select-none"
      onClick={startAnimation}
      onTouchStart={startAnimation}
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10 mix-blend-screen"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1563277267-0e9f2dabb9ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxyZXRybyUyMGFyY2FkZSUyMG1hY2hpbmV8ZW58MHx8fHwxNzc2NDMxOTY5fDA&ixlib=rb-4.1.0&q=85)' }}
      />

      <div className="relative z-10 text-center">
        {/* Title: 90" */}
        <div className="h-28 flex items-center justify-center">
          <h1 className="font-heading text-7xl sm:text-9xl uppercase tracking-widest text-[#E61C24] drop-shadow-[0_0_20px_rgba(230,28,36,0.9)]">
            {TITLE_CHARS.slice(0, titleIndex).map((char, i) => (
              <span
                key={i}
                className="inline-block animate-[bounceIn_0.2s_ease-out]"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                {char}
              </span>
            ))}
          </h1>
        </div>

        {/* Subtitle: TO WIN */}
        <div className="h-28 flex items-center justify-center">
          <h2 className="font-heading text-7xl sm:text-9xl uppercase tracking-widest text-[#E61C24] drop-shadow-[0_0_20px_rgba(230,28,36,0.9)]">
            {SUBTITLE_CHARS.slice(0, subtitleIndex).map((char, i) => (
              <span
                key={i}
                className="inline-block"
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </h2>
        </div>

        {/* Tap to start (before animation) */}
        {!audioStarted && (
          <p className="font-mono text-lg text-[#FFF200] mt-8 animate-[pulse_1.5s_ease-in-out_infinite]">
            TAP TO START
          </p>
        )}

        {/* Buttons */}
        {showEnter && (
          <div className="mt-10 animate-[fadeIn_0.5s_ease-out] space-y-3 w-full max-w-xs mx-auto">
            <button
              data-testid="splash-login-btn"
              onClick={(e) => { e.stopPropagation(); onLogin(); }}
              className="bg-[#E61C24] hover:bg-[#FF333B] text-white font-heading text-xl uppercase tracking-[0.2em] py-4 w-full border-b-4 border-red-900 active:border-b-0 active:translate-y-1 transition-all shadow-[0_0_20px_rgba(230,28,36,0.7)] hover:shadow-[0_0_35px_rgba(255,51,59,1)]"
            >
              CONNEXION
            </button>
            <button
              data-testid="splash-demo-btn"
              onClick={(e) => { e.stopPropagation(); onDemo(); }}
              className="bg-transparent hover:bg-[#00AEEF]/10 text-[#00AEEF] font-heading text-xl uppercase tracking-[0.2em] py-4 w-full border-2 border-[#00AEEF] active:translate-y-0.5 transition-all shadow-[inset_0_0_10px_rgba(0,174,239,0.2)] hover:shadow-[0_0_20px_rgba(0,174,239,0.5)]"
            >
              DEMO INVITE
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
